import { Avatar, AvatarFallback, AvatarImage } from './ui/avatar';
import { Progress } from './ui/progress';
import { Tooltip, TooltipContent, TooltipTrigger } from './ui/tooltip';
import { avatarTint, iniciales } from '../lib/identity';
import { cn } from '../lib/utils';

/**
 * Quién está en la clínica y con cuánto trabajo.
 *
 * Antes mostraba "0 citas" para todos porque leía un campo (`citas_hoy`) que el
 * backend no manda: el widget MENTÍA. Ahora cuenta las citas de hoy que el
 * dashboard ya trae, así que el número siempre es real.
 */
export const DoctorWidget = ({ doctors = [], citasHoy = [], cargando = false }) => {
  const activos = doctors.filter((d) => d.activo);

  const carga = (doctorId) => citasHoy.filter((c) => c.doctor_id === doctorId);
  const maximo = Math.max(...doctors.map((d) => carga(d.id).length), 1);

  const estadoDe = (id) => {
    const citas = carga(id);
    if (citas.some((c) => c.estado === 'en_sala')) return { label: 'En consulta', tono: 'text-warning bg-warning/15' };
    const proxima = citas
      .filter((c) => ['citado', 'confirmada'].includes(c.estado))
      .sort((a, b) => a.hora_inicio.localeCompare(b.hora_inicio))[0];
    if (proxima) return { label: `Sigue ${proxima.hora_inicio}`, tono: 'text-primary bg-primary/[0.09]' };
    if (citas.length) return { label: 'Terminó su día', tono: 'text-muted-foreground bg-muted' };
    return { label: 'Sin citas hoy', tono: 'text-muted-foreground bg-muted' };
  };

  return (
    <section className="overflow-hidden rounded-xl border border-border/70 bg-card shadow-sm" data-testid="doctor-widget">
      <header className="flex items-center justify-between gap-2 px-4 pb-2 pt-4">
        <div>
          <h3 className="font-display text-[15px] font-bold tracking-tight">Equipo en turno</h3>
          <p className="text-[11.5px] text-muted-foreground tnum">
            {activos.length} {activos.length === 1 ? 'profesional' : 'profesionales'} · {citasHoy.length} citas hoy
          </p>
        </div>
        <span className="flex items-center gap-1.5 rounded-full bg-success/10 px-2 py-0.5 text-[11px] font-semibold text-success">
          <span className="h-1.5 w-1.5 rounded-full bg-success animate-pulse-soft" /> en vivo
        </span>
      </header>

      <div className="divide-y divide-border/60">
        {cargando && [0, 1, 2].map((i) => (
          <div key={i} className="flex items-center gap-3 px-4 py-3">
            <div className="skeleton h-10 w-10 rounded-full" />
            <div className="flex-1 space-y-2">
              <div className="skeleton h-3 w-32" />
              <div className="skeleton h-2.5 w-20" />
            </div>
          </div>
        ))}

        {!cargando && doctors.map((doctor) => {
          const citas = carga(doctor.id);
          const estado = estadoDe(doctor.id);
          return (
            <div
              key={doctor.id}
              className={cn('flex items-center gap-3 px-4 py-3 transition-colors hover:bg-muted/40', !doctor.activo && 'opacity-55')}
              data-testid={`doctor-${doctor.id}`}
            >
              <Tooltip>
                <TooltipTrigger asChild>
                  <div className="relative shrink-0">
                    <Avatar className="h-10 w-10 ring-2 ring-offset-2 ring-offset-card" style={{ '--tw-ring-color': doctor.color }}>
                      <AvatarImage src={doctor.avatar_url} alt={doctor.nombre} />
                      <AvatarFallback className="text-[11px] font-semibold text-white" style={avatarTint(doctor.nombre)}>
                        {iniciales(doctor.nombre, 'DR')}
                      </AvatarFallback>
                    </Avatar>
                    {citas.some((c) => c.estado === 'en_sala') && (
                      <span className="absolute -bottom-0.5 -right-0.5 h-3 w-3 rounded-full border-2 border-card bg-warning" />
                    )}
                  </div>
                </TooltipTrigger>
                <TooltipContent side="left">
                  {doctor.telefono || doctor.email}
                </TooltipContent>
              </Tooltip>

              <div className="min-w-0 flex-1">
                <div className="flex items-baseline justify-between gap-2">
                  <p className="truncate text-[13.5px] font-semibold">{doctor.nombre}</p>
                  <span className="shrink-0 text-[11px] text-muted-foreground tnum">
                    {citas.length} {citas.length === 1 ? 'cita' : 'citas'}
                  </span>
                </div>
                <p className="truncate text-[11.5px] text-muted-foreground">{doctor.especialidad}</p>

                <div className="mt-1.5 flex items-center gap-2">
                  <Progress
                    value={(citas.length / maximo) * 100}
                    className="h-1"
                    style={{ '--progress-color': doctor.color }}
                  />
                  <span className={cn('shrink-0 rounded-full px-1.5 py-0.5 text-[10px] font-semibold', estado.tono)}>
                    {estado.label}
                  </span>
                </div>
              </div>
            </div>
          );
        })}

        {!cargando && doctors.length === 0 && (
          <p className="px-4 py-8 text-center text-sm text-muted-foreground">
            Todavía no hay profesionales registrados.
          </p>
        )}
      </div>
    </section>
  );
};

export default DoctorWidget;
